import { ReactNode, useEffect, useMemo } from 'react';

import { ReceptionistsContext, receptionistsContext } from './receptionistsContext';
import { Spinner } from '../../components/Spinner';
import { useAuth } from '../../hooks/useAuth';
import { useFetchCustomRules } from '../../hooks/useFetchCustomRules';
import { useFetchUser } from '../../hooks/useFetchUser';
import { useStorageState } from '../../hooks/useStorageState';
import { ToastManager } from '../toast/utils';
import { t } from '../translation/translation';

type Props = {
  children: ReactNode;
};

const ACTIVE_RECEPTIONIST_STORAGE_KEY = 'active_receptionist';

export const ReceptionistsProvider = ({ children }: Props) => {
  const { data, isLoading, isFetched } = useFetchCustomRules();
  const [activeReceptionist, setActiveReceptionist] = useStorageState(ACTIVE_RECEPTIONIST_STORAGE_KEY);

  useEffect(() => {
    if (activeReceptionist === null && data?.invoiceMention.values && data.invoiceMention.values.length > 0) {
      setActiveReceptionist(data.invoiceMention.values[0]);
    }
  }, [activeReceptionist, data?.invoiceMention.values, setActiveReceptionist]);

  const contextValue = useMemo<ReceptionistsContext>(
    () => ({
      isLoading,
      isFetched,
      receptionists: data?.invoiceMention.values ?? [],
      activeReceptionist,
      setActiveReceptionist,
    }),
    [activeReceptionist, data?.invoiceMention, isFetched, isLoading, setActiveReceptionist],
  );

  const { data: user, isLoading: isUserLoading } = useFetchUser();
  const { logoutMutation } = useAuth();
  useEffect(() => {
    if (!isUserLoading && user?.businessLine !== 'HOTELS') {
      ToastManager.show({ severity: 'error', message: t('error.not-allowed') });
      logoutMutation.mutate();
    }
  }, [isUserLoading, user, logoutMutation]);
  if (isUserLoading || user?.businessLine !== 'HOTELS') {
    return (
      <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return <receptionistsContext.Provider value={contextValue}>{children}</receptionistsContext.Provider>;
};
